// ============================================================================
// INTERESTS — the derived strings.
//
// Every one of these is in README §State Management's "Derived, never stored"
// list: an age, a shown-count, a shortened URL. None of them is a field, and
// none of them reads the clock — `today` is passed in from the data contract's
// own clock, which is what keeps demo mode reproducible and future visual
// baselines stable.
// ============================================================================

import { daysBetween, monthName, toDate, type ISODate, type ISOTimestamp, type InterestSave, type SaveSource } from '@app/shared'

/** The calendar day a timestamp belongs to. The offset is the owner's, so the prefix is the day. */
export function dayOf(at: ISOTimestamp): ISODate {
  return at.slice(0, 10)
}

/**
 * `3d` · `1w` · `1mo` · `today` — the age stamp under every save card, in the
 * mockup's own units.
 *
 * Weeks are rounded and months floored, and both are deliberate: `22d` reads as
 * `3w` because that is what it is to a person, while `35d` reads as `1mo`
 * rather than `1mo` rounded up to two. Nothing here ever says `0d`.
 */
export function savedAgo(savedAt: ISOTimestamp, today: ISODate): string {
  const days = daysBetween(dayOf(savedAt), today)
  if (days <= 0) return 'today'
  if (days < 7) return `${days}d`
  if (days < 30) return `${Math.round(days / 7)}w`
  return `${Math.max(1, Math.floor(days / 30.4))}mo`
}

/** `edited 3d ago` under MY NOTE. A note touched today says `edited today`. */
export function noteEdited(at: ISOTimestamp, today: ISODate): string {
  const ago = savedAgo(at, today)
  return ago === 'today' ? 'edited today' : `edited ${ago} ago`
}

/** `Mar 2026` — the cluster footer's oldest-save stamp. */
export function monthYear(at: ISOTimestamp): string {
  const date = dayOf(at)
  return `${monthName(date, true)} ${toDate(date).getUTCFullYear()}`
}

// ── Provenance strings ──────────────────────────────────────────────────────

const SOURCE_NAME: Record<SaveSource, string> = {
  reddit: 'Reddit',
  youtube: 'YouTube',
  article: 'Web',
  x: 'X',
}

export function sourceName(source: SaveSource): string {
  return SOURCE_NAME[source]
}

/**
 * `Reddit · r/StableDiffusion` in the detail pane, where the card showed only
 * `r/StableDiffusion`. When the platform IS the locator (a YouTube save's
 * detail is `YouTube`), it is said once rather than twice — one rule, no
 * per-source special case.
 */
export function detailSourceLine(save: InterestSave): string {
  const name = sourceName(save.source)
  return name === save.sourceDetail ? name : `${name} · ${save.sourceDetail}`
}

/**
 * `reddit.com/r/StableDiffusion/…/wan22_motion`.
 *
 * The middle of a long path is elided, never the end: the last segment is the
 * only part of a Reddit URL a person can recognise, and a URL truncated with a
 * trailing ellipsis throws away the one piece that identifies it.
 */
export function displayUrl(url: string): string {
  const bare = url.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '')
  if (bare.length <= 46) return bare
  const parts = bare.split('/')
  if (parts.length <= 4) return bare
  return `${parts[0]}/${parts[1]}/${parts[2]}/…/${parts[parts.length - 1]}`
}

/** `18 min` — the badge on a video save's media band. */
export function durationLabel(durationSec: number): string {
  return `${Math.max(1, Math.round(durationSec / 60))} min`
}

// ── Cluster and library lines ───────────────────────────────────────────────

/**
 * `14 saves · 10 shown · newest first`.
 *
 * `shown` is stated because it is not always `count`, and a grid that silently
 * shows ten of fourteen is the app quietly deciding what the library contains.
 * When everything is on screen the clause is dropped rather than padded out to
 * `14 shown`, which would be noise.
 */
export function libraryLine(count: number, shown: number): string {
  const saves = `${count} ${count === 1 ? 'save' : 'saves'}`
  return shown < count ? `${saves} · ${shown} shown · newest first` : `${saves} · newest first`
}

/**
 * `4 more in AI video models` — and plain `4 more` when nothing has been grouped, because
 * the library still has a tail when the clustering has not run. Naming a cluster that does
 * not exist would be the expander claiming a grouping the nightly job never made. Only ever
 * rendered when the number is above zero.
 */
export function moreInCluster(remaining: number, label?: string): string {
  return label ? `${remaining} more in ${label}` : `${remaining} more`
}

/**
 * `oldest save in this cluster: Mar 2026`, the quietest line on the screen — and
 * `oldest save: Mar 2026` when there is no cluster for it to be in.
 *
 * The clause is dropped rather than reworded: the fact being stated (this library reaches
 * back to March) is the same one either way, and it is only the scope of "this" that the
 * ungrouped library cannot supply.
 */
export function oldestLine(at: ISOTimestamp, clustered: boolean): string {
  return `oldest save${clustered ? ' in this cluster' : ''}: ${monthYear(at)}`
}

/** The band's middle count. Plural-safe, because a one-item library is a real Tuesday. */
export function itemsLine(total: number): string {
  return `${total} ${total === 1 ? 'item' : 'items'}`
}
